import { useState } from "react";
import { useParams, useNavigate, NavLink, Outlet } from "react-router-dom";

import { useAppData } from "@/contexts/AppDataContext";
import { useBrandAutoSwitch } from "@/hooks/useBrandAutoSwitch";
import { useListingDrawer } from "@/hooks/useListingDrawer";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Database,
  Layers,
  Lightbulb,
  FlaskConical,
  FileText,
  ExternalLink,
  Eye,
  MoreVertical,
  ShieldAlert,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteHeroAsinDialog, DeleteHeroAsinTarget } from "@/components/shared/DeleteHeroAsinDialog";
import { LifecycleStateBadge } from "@/components/shared/LifecycleStateBadge";
import { LifecycleStateModal } from "@/components/shared/LifecycleStateModal";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { ProhibitedKeywordsOverviewModal } from "@/components/search-terms/ProhibitedKeywordsOverviewModal";
import { ListingContentPanel } from "@/components/listing-content";
import { cn } from "@/lib/utils";
import { RoutePath } from "@/routes";
import { productApi } from "@/resources/product";
import { keywordBankRunApi } from "@/resources/keyword-bank-run";
import { isKeywordBankImporting } from "@/utils/keywordBankImporting";
import FullScreenLoader from "@/components/ui/full-screen-loader";
import { AMAZON_ASIN_URL } from "@/constants/amazon";
import { PRODUCT_IMAGE_PLACEHOLDER } from "@/constants/images";
import { ProductDetailOutletContext } from "@/contexts/ProductDetailContext";

const TABS = [
  // AUT-430: renamed to "Strategy". The slug stays seo-insights so existing links
  // and bookmarks keep working -- changing the route is a separate decision.
  { slug: "seo-insights", label: "Strategy", icon: Lightbulb },
  { slug: "ab-tests", label: "A/B Tests", icon: FlaskConical },
  { slug: "content-scorecard", label: "Amazon AI Score", icon: Eye },
  { slug: "keywords", label: "Keywords", icon: Database },
  { slug: "landscapes", label: "Landscapes", icon: Layers },
  { slug: "search-terms", label: "Search Terms", icon: FileText },
];

function formatUsd(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "N/A";
  const n = Number(value);
  if (Number.isNaN(n)) return "N/A";
  return `$${n.toFixed(2)}`;
}

function formatRating(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "N/A";
  const n = Number(value);
  if (Number.isNaN(n)) return "N/A";
  return n.toFixed(2);
}

function formatIntStat(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "N/A";
  const n = Number(value);
  if (Number.isNaN(n)) return "N/A";
  return String(Math.round(n));
}

export default function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const [asinToDelete, setAsinToDelete] = useState<DeleteHeroAsinTarget | null>(null);
  const [prohibitedKeywordsOpen, setProhibitedKeywordsOpen] = useState(false);
  const [lifecycleModalOpen, setLifecycleModalOpen] = useState(false);
  const readOnly = useViewerReadOnly();

  const { isResolvingBrand } = useBrandAutoSwitch(productId);
  const { isOpen: isDrawerOpen, close: closeDrawer, toggle: toggleDrawer } = useListingDrawer();

  const { data: productDetail, isPending: isProductDetailPending } =
    productApi.useGetHeroProductDetail(productId, selectedBrandId, {
      includeKeywords: false,
    });

  // AUT-196 follow-up: while the product's keyword bank is still seeding, the
  // Keywords / Pg 1 KPI cells show a spinner instead of a not-yet-computed
  // number. Driven by the same keyword-bank-run signal the Keyword Bank tab
  // reads; scoped by product id alone (no brand_id).
  const { data: keywordBankRunResponse } =
    keywordBankRunApi.useGetKeywordBankRun(Number(productId));
  const isImporting = isKeywordBankImporting(keywordBankRunResponse?.run);

  const showStats =
    (productDetail?.keywords_with_rank_slot_count &&
      productDetail?.competitors_avg_keywords_with_rank_slot) ||
    (productDetail?.keywords_pg1_count &&
      productDetail?.competitors_avg_keywords_pg1) ||
    (productDetail?.price && productDetail?.competitors_avg_price) ||
    (productDetail?.rating && productDetail?.competitors_avg_rating);

  if (isResolvingBrand || isProductDetailPending) {
    return <FullScreenLoader />;
  }

  if (!productDetail) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Product not found
        </h2>
        <p className="text-muted-foreground mb-4">
          The product you're looking for doesn't exist.
        </p>
        <Button onClick={() => navigate(RoutePath.Products)}>
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Button>
      </div>
    );
  }

  const heroVsCompetitorStats = [
    {
      label: "Price",
      hero: formatUsd(productDetail.price),
      comp: formatUsd(productDetail.competitors_avg_price),
    },
    {
      label: "Rating",
      hero: formatRating(productDetail.rating),
      comp: formatRating(productDetail.competitors_avg_rating),
    },
    {
      label: "Keywords",
      hero: formatIntStat(productDetail.keywords_with_rank_slot_count),
      comp: formatIntStat(productDetail.competitors_avg_keywords_with_rank_slot),
    },
    {
      label: "Pg 1",
      hero: formatIntStat(productDetail.keywords_pg1_count),
      comp: formatIntStat(productDetail.competitors_avg_keywords_pg1),
    },
  ];

  const baseUrl = `/products/${productId}`;

  const context: ProductDetailOutletContext = { productDetail };

  return (
    <div className="bg-card -m-6 min-w-0 min-h-screen">
      <div className="flex-shrink-0 space-y-4 px-6 pt-6 min-w-0">
        <button
          onClick={() => navigate(RoutePath.Products)}
          className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-fit"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all products
        </button>

        <div className="flex items-start gap-4 -mt-1">
          {/*
            The drawer toggle sits under the product image, not in the
            header's right-hand action row: it belongs to the column the
            drawer opens into, and reads as a control for the panel below it
            rather than one more product-level action next to Delete
            (`AUT-297`; prototype `ProductDetail.tsx`).
          */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            <div className="w-24 h-24 rounded-lg bg-white overflow-hidden border-2 border-card">
              {/*
                AUT-279: square box, rectangular Amazon source image —
                object-cover centre-cropped the product out of its own header.
                object-contain letterboxes inside the box instead; do not
                reintroduce object-cover. Reopened: the client rejected the grey bars,
                so the box is bg-white — Amazon source images are white-backed and
                the bars vanish into the photo.
              */}
              <img
                src={productDetail.image_url || PRODUCT_IMAGE_PLACEHOLDER}
                alt={productDetail.name}
                className="w-full h-full object-contain"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-sm"
              data-testid="listing-drawer-toggle"
              onClick={toggleDrawer}
            >
              Current listing
              {isDrawerOpen ? (
                <PanelLeftClose className="h-4 w-4" />
              ) : (
                <PanelLeftOpen className="h-4 w-4" />
              )}
            </Button>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-foreground leading-snug max-w-md">
              {productDetail.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {productDetail.asin}
              </span>
              <span className="text-xs text-muted-foreground">|</span>
              <a
                href={`${AMAZON_ASIN_URL}/${productDetail.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Amazon US
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            {/*
              The lifecycle designation, in its own row under the ASIN line — the prototype's
              composition (`signal-v3/src/pages/ProductDetail.tsx`, the `mt-2 flex items-center
              gap-2` row). The badge renders nothing at all for `unassigned`, there as here, so an
              undesignated product shows no row and the Products page keeps being the place a
              designation is first set.

              A viewer is handed no `onChangeRequest`, the same gate both Products views use, so the
              badge still renders and only the hover key's "Change lifecycle state" footer is absent.
            */}
            <div className="mt-2 flex items-center gap-2">
              <LifecycleStateBadge
                state={productDetail.lifecycle_state}
                onChangeRequest={readOnly ? undefined : () => setLifecycleModalOpen(true)}
              />
            </div>
          </div>

          {showStats || isImporting ? (
            <div className="flex-shrink-0">
              <div className="grid grid-cols-2 border border-border rounded-[3px] overflow-hidden">
                {heroVsCompetitorStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={cn(
                      "px-3 py-1.5 text-center min-w-[100px] tabular-nums",
                      i % 2 === 0 && "border-r border-border",
                      i < 2 && "border-b border-border",
                    )}
                  >
                    <p className="text-[10px] text-muted-foreground font-medium">
                      {stat.label}
                    </p>
                    {/*
                      AUT-196 follow-up: only the two keyword-derived cells are
                      pending while the bank imports; Price and Rating come from
                      the product row and render normally throughout.
                    */}
                    {isImporting && (stat.label === "Keywords" || stat.label === "Pg 1") ? (
                      <p className="flex justify-center py-0.5">
                        <Loader2
                          className="h-3.5 w-3.5 animate-spin text-muted-foreground"
                          aria-hidden="true"
                          data-testid="header-stat-importing-spinner"
                        />
                      </p>
                    ) : (
                      <p className="text-sm">
                        <span className="text-foreground font-bold">
                          {stat.hero}
                        </span>
                        <span className="text-muted-foreground"> / </span>
                        <span className="text-foreground">{stat.comp}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 text-center">
                <span className="text-foreground font-bold">Hero</span> /
                competitor avg.
              </p>
            </div>
          ) : null}

          <div className="flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setProhibitedKeywordsOpen(true)}>
                  <ShieldAlert className="h-4 w-4 mr-2" />
                  Prohibited Keywords
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setAsinToDelete({ id: productDetail.id, name: productDetail.name })}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

      </div>

      {/*
        The tab strip belongs to the right column, not to the page header:
        the drawer opens *beside* the tabs and pushes them right, it does not
        slide under a full-width strip. That is what the prototype's
        "left drawer + right column with tabs + content" body does, and the
        two columns share this row's `pt-6` so the panel's first line and the
        tabs sit on the same baseline.

        `pb-9` is the gap under the drawer: with the panel taller than the tab
        content, the card used to run into the bottom edge of the page
        (`AUT-299`). Horizontal padding stays on the two columns rather than
        on this row -- the tab pages carry their own `p-6`, so a `px` here
        would indent their content twice.
      */}
      <div className="flex min-w-0 pt-6 pb-9">
        {/*
          The panel is kept mounted and hidden by width rather than
          unmounted, so the 300 ms collapse animates over its real content
          and the local cooldown state survives a close. `active` is what
          keeps that from costing a request: a collapsed panel fetches
          nothing (`listing.api.ts § useGetListing`).

          The aside is static on purpose: this wrapper is `overflow-hidden`,
          so a `sticky top-*` on the aside never stuck, it only pushed the
          panel down by up to 24px as the right column grew (`AUT-434`).
        */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-out",
            // 320 of panel plus the page's own 24 of left padding, so the
            // card lines up with the header above it and both collapse
            // together (`AUT-297`).
            isDrawerOpen
              ? "w-[344px] pl-6 mr-6 opacity-100 translate-x-0"
              : "w-0 pl-0 mr-0 opacity-0 -translate-x-4",
          )}
          aria-hidden={!isDrawerOpen}
          data-testid="listing-drawer-wrapper"
        >
          <aside className="w-[320px]" data-testid="listing-drawer-panel">
            {selectedBrandId ? (
              <ListingContentPanel
                productId={productDetail.id}
                brandId={selectedBrandId}
                asin={productDetail.asin}
                productTitle={productDetail.name}
                heroShow={{
                  price: productDetail.price,
                  rating: productDetail.rating,
                  reviews: productDetail.reviews,
                }}
                active={isDrawerOpen}
                onClose={closeDrawer}
              />
            ) : null}
          </aside>
        </div>

        <div className="flex-1 min-w-0 flex flex-col">
          {/*
            `px-6` lines the labels up with the tab pages' own `p-6`. The strip
            scrolls horizontally because it no longer has the page's width to
            spread over -- with the drawer open it is 344px narrower, which on
            a laptop is not enough for six tabs. `w-max min-w-full` keeps the
            rule under them spanning the full column when they do fit.
          */}
          <div className="overflow-x-auto px-6">
            <div className="flex w-max min-w-full flex-nowrap items-center gap-0 border-b border-border">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.slug}
                  to={`${baseUrl}/${tab.slug}`}
                  className={({ isActive }) =>
                    cn(
                      "flex shrink-0 items-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px",
                      isActive
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                    )
                  }
                  end={tab.slug !== "landscapes"}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </NavLink>
              ))}
            </div>
          </div>

          <Outlet context={context} />
        </div>
      </div>

      {selectedBrandId ? (
        <DeleteHeroAsinDialog
          asin={asinToDelete}
          brandId={selectedBrandId}
          onCancel={() => setAsinToDelete(null)}
          onDeleted={() => {
            setAsinToDelete(null);
            navigate(RoutePath.Products);
          }}
        />
      ) : null}

      <ProhibitedKeywordsOverviewModal
        open={prohibitedKeywordsOpen}
        onOpenChange={setProhibitedKeywordsOpen}
        brandId={selectedBrandId}
        productId={productDetail.id}
        productName={productDetail.name}
      />

      {/*
        The same single write path the Products page uses, hosted once here. Brand-gated like the
        delete dialog above, and not additionally `readOnly`-gated: a viewer is handed no
        `onChangeRequest`, so nothing can open it.

        Saving patches this page's cached detail row rather than invalidating it — this page returns
        a `<FullScreenLoader />` while that query is pending, so an invalidation would blank it
        mid-save (`decisions/a-lifecycle-save-patches-the-cached-product-row.md`).
      */}
      {selectedBrandId && (
        <LifecycleStateModal
          open={lifecycleModalOpen}
          onOpenChange={setLifecycleModalOpen}
          product={productDetail}
          brandId={selectedBrandId}
        />
      )}
    </div>
  );
}
